import { MapLocation } from 'app/agriculture/queries/types/MapLocation';
import { useCallback, useState } from 'react';
import { RenderedMapMarker } from 'app/agriculture/components/Map/RenderedMapMarker/RenderedMapMarker';
import { Collapse } from '@mui/material';
import { Input } from 'app/agriculture/forms/components/Input/Input';
import { AgCheckbox } from 'app/agriculture/components/form/AgCheckbox/AgCheckbox';
import { SimpleGrid } from 'app/agriculture/components/SimpleGrid/SimpleGrid';
import { InfoIcon, LocationIcon } from 'app/agriculture/icons';
import { BannerContent } from 'app/agriculture/Banner/BannerContent';
import { EntityType } from 'app/agriculture/models/Entities';
import './SpecificLocationField.scss';

const LOCATION_DECIMAL_PRECISION_STEP = 0.000001;

interface SpecificLocationFieldProps {
    label?: string;
    initialValue?: MapLocation | null;
    onChange: (value: MapLocation | null) => void;
    entityType: EntityType;
    isPin?: boolean;
    defaultCoords?: MapLocation | null;
}

export function SpecificLocationField({
    label = 'Specific Location',
    initialValue,
    onChange,
    entityType,
    isPin = false,
    defaultCoords,
}: SpecificLocationFieldProps) {
    const [value, setValue] = useState<MapLocation | null | undefined>(initialValue);
    const [expanded, setExpanded] = useState<boolean>(!!initialValue);

    const handleValueChange = useCallback(
        (value?: MapLocation | null) => {
            setValue(value);

            if (value != null) {
                onChange(expanded ? value : null);
            }
        },
        [expanded, onChange],
    );

    const handleExpandedChange = useCallback(
        (expanded: boolean) => {
            setExpanded(expanded);
            if (value != null) {
                onChange(expanded ? value : null);
            } else {
                setValue(defaultCoords);
                onChange(defaultCoords ?? null);
            }
        },
        [value, onChange, defaultCoords],
    );

    return (
        <div className="SpecificLocationField">
            <AgCheckbox
                id="specific-location-checkbox"
                label={label}
                isChecked={expanded}
                onChange={handleExpandedChange}
            />
            <Collapse in={expanded}>
                <SimpleGrid columns={2}>
                    <Input
                        type="number"
                        step={LOCATION_DECIMAL_PRECISION_STEP}
                        label="Lat"
                        value={value?.latitude}
                        onChange={(e) => {
                            handleValueChange({
                                latitude: Number(e.currentTarget.value || 0),
                                longitude: value?.longitude ?? 0,
                            });
                        }}
                    />
                    <Input
                        type="number"
                        step={LOCATION_DECIMAL_PRECISION_STEP}
                        label="Lon"
                        value={value?.longitude}
                        onChange={(e) => {
                            handleValueChange({
                                latitude: value?.latitude ?? 0,
                                longitude: Number(e.currentTarget.value || 0),
                            });
                        }}
                    />
                </SimpleGrid>
                {expanded && (
                    <RenderedMapMarker
                        isPin={isPin}
                        value={value ?? undefined}
                        onChange={handleValueChange}
                    />
                )}
                <BannerContent isOpen={expanded}>
                    <>
                        <section className="banner-section">
                            <LocationIcon color="#3098E8" size={18} />
                            <h4>Set location tool</h4>
                        </section>
                        <section className="banner-section">
                            <InfoIcon color="#3098E8" size={18} />
                            <p>{`Left click to place a ${entityType}`}</p>
                        </section>
                    </>
                </BannerContent>
            </Collapse>
        </div>
    );
}
